import axios from "axios";
import { STORAGE_KEYS, COOKIE_KEYS } from "../constants";
import { isTokenExpired } from "../utils/tokenUtils";
import { authService } from "../services/authService";
import { store } from "../store/store";
import { tokenRefreshed, logout } from "../store/slices/authSlice";
import { clearAuthData } from "../utils/authSync";
import { getCookie, setCookie, deleteCookie } from "../utils/cookieUtils";

if (!process.env.REACT_APP_API_BASE_URL) {
  // Fail loudly during development; in production the build pipeline must provide this.
  console.error(
    "[api] REACT_APP_API_BASE_URL is not set. All API requests will fail.",
  );
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  validateStatus: (status) => status >= 200 && status < 300,
  withCredentials: true,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const tokenRaw = getCookie(COOKIE_KEYS.ACCESS_TOKEN);
    if (tokenRaw) {
      try {
        const tokenData = JSON.parse(tokenRaw);
        const refreshToken = getCookie(COOKIE_KEYS.REFRESH_TOKEN);
        if (refreshToken && !tokenData.refresh_token) {
          tokenData.refresh_token = refreshToken;
        }

        if (isTokenExpired(tokenData)) {
          deleteCookie(COOKIE_KEYS.ACCESS_TOKEN);
          return config;
        }

        config.headers.Authorization = `Bearer ${tokenData.access_token}`;
      } catch (error) {
        console.error("Invalid token format:", error);
        deleteCookie(COOKIE_KEYS.ACCESS_TOKEN);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const tokenRaw = getCookie(COOKIE_KEYS.ACCESS_TOKEN);
      const refreshToken = getCookie(COOKIE_KEYS.REFRESH_TOKEN);

      if (tokenRaw && refreshToken) {
        try {
          const tokenData = JSON.parse(tokenRaw);

          // Backend returns both new access_token and new refresh_token on every refresh.
          // The new refresh_token must be saved — the old one is invalidated immediately.
          const newToken = await authService.refreshToken({
            access_token: tokenData.access_token,
            refresh_token: refreshToken,
          });

          const newTokenWithTimestamp = {
            ...newToken,
            issued_at: Date.now() / 1000,
          };

          const { refresh_token: newRt, ...newAccessData } = newTokenWithTimestamp;
          setCookie(COOKIE_KEYS.ACCESS_TOKEN, JSON.stringify(newAccessData), 1);
          setCookie(COOKIE_KEYS.REFRESH_TOKEN, newRt, 7);

          store.dispatch(tokenRefreshed({ token: newTokenWithTimestamp }));

          originalRequest.headers.Authorization = `Bearer ${newToken.access_token}`;
          return api(originalRequest);
        } catch (refreshError: unknown) {
          console.error("Token refresh failed in axios interceptor:", refreshError);

          const isRegistrationFlow = localStorage.getItem(STORAGE_KEYS.REGISTRATION_TOKEN);
          if (!isRegistrationFlow) {
            clearAuthData();
            store.dispatch(logout());
          }
          return Promise.reject(refreshError);
        }
      }

      const isRegistrationFlow = localStorage.getItem(STORAGE_KEYS.REGISTRATION_TOKEN);
      const wasAuthenticated = !!(tokenRaw || refreshToken);

      if (!isRegistrationFlow && wasAuthenticated) {
        clearAuthData();
        store.dispatch(logout());
      }
    }

    return Promise.reject(error);
  },
);

export default api;
